"use client";

import type { ReactElement } from "react";
import { memo, useState } from "react";
import {
  isStockFilterSelected,
  mapStockFilterCategoryToFilterKey,
} from "../utils/stockListingHelpers";
import {
  type StockFilterCategoryKey,
  useStockListingFilters,
} from "../context/StockListingFiltersContext";
import { pushStockFilterCtEvent, stockFilterCtConstants } from "./stockFiltersCt";

const DEFAULT_VISIBLE = 10;

function mapCategoryToFilterKey(categoryKey: string) {
  switch (categoryKey) {
    case "alphabetical":
      return "alphabetic" as const;
    case "exchange":
      return "index" as const;
    case "mktCapType":
      return "mktcap" as const;
    case "sector":
      return "sector" as const;
    default:
      return null;
  }
}

/** Parity with `StocksFilterFunctions.updateDataLayer` / `handleCheckboxChangeDesktop` (ui.solid). */
function pushStocksFilterCheckboxClick(categoryName: string, optionValue: string) {
  pushStockFilterCtEvent({
    event: stockFilterCtConstants.checkboxClick,
    element: stockFilterCtConstants.checkboxElement,
    sectionTitle: optionValue,
    filterCategory: categoryName,
    filterSubCategory: optionValue,
  });
}

/** Parity with `handleAlphabeticalSelect` (desktop): tab_click + letter as category for CT. */
function pushStocksFilterAlphabetTabClick(letter: string) {
  pushStockFilterCtEvent({
    event: stockFilterCtConstants.tabClick,
    element: stockFilterCtConstants.tabElement,
    sectionTitle: letter,
    filterCategory: letter,
  });
}

const StockListingFiltersSidebar = memo(() => {
  const { filterCategories, isLoading, error, activeFilters, setActiveFilters, uiLabels } =
    useStockListingFilters();

  const [showAllByKey, setShowAllByKey] = useState<Record<string, boolean>>({});
  const [searchQueryByKey, setSearchQueryByKey] = useState<Record<string, string>>({});

  const toggleOption = (
    categoryKey: StockFilterCategoryKey,
    categoryName: string,
    optionValue: string,
    optionLabel?: string,
  ) => {
    const filterKey = mapCategoryToFilterKey(categoryKey);
    if (!filterKey) return;

    if (categoryKey === "alphabetical") {
      const prevList = (activeFilters.alphabetic ?? []).map(String);
      const alreadySelected = prevList[0] === optionValue && prevList.length === 1;
      if (!alreadySelected) {
        pushStocksFilterAlphabetTabClick(optionValue);
      }
      setActiveFilters((prev) => ({
        ...prev,
        alphabetic: alreadySelected ? [] : [optionValue],
      }));
      return;
    }

    const trackingValue =
      categoryKey === "exchange" ? (optionLabel ?? optionValue) : optionValue;
    pushStocksFilterCheckboxClick(categoryName, trackingValue);

    setActiveFilters((prev) => {
      const prevList = (prev?.[filterKey] ?? []).map(String);

      const nextList = prevList.includes(optionValue)
        ? prevList.filter((v) => v !== optionValue)
        : [...prevList, optionValue];

      // Backend expects numeric IDs for `index` (exchange).
      if (filterKey === "index") {
        const normalizedIndex = nextList
          .map((v) => Number(v))
          .filter((n) => !Number.isNaN(n));
        return { ...prev, [filterKey]: normalizedIndex };
      }

      return { ...prev, [filterKey]: nextList };
    });
  };

  if (isLoading) {
    return (
      <div className="p-3">
        <div className="text-[12px] text-gray-600">{uiLabels.loadingText ?? "Loading filters\u2026"}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3">
        <div className="text-[12px] text-red-600">{uiLabels.filtersErrorText ?? "Unable to load filters"}</div>
        <div className="mt-1 text-[11px] text-gray-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="box-border w-full">
      <div className="flex flex-col gap-4 p-3 pl-6">
        {filterCategories.map((cat) => {
          const filterKey = mapStockFilterCategoryToFilterKey(cat.key);
          const selectedList = filterKey ? (activeFilters[filterKey] ?? []) : [];
          const showAll = showAllByKey[cat.key] ?? false;
          let visibleOptions = cat.options;
          if (cat.key !== "alphabetical") {
            visibleOptions = showAll ? cat.options : cat.options.slice(0, DEFAULT_VISIBLE);
          }
          const hasOverflow = cat.options.length > DEFAULT_VISIBLE;
          const overflowToggleText = showAll
            ? uiLabels.showLessText ?? "Show less"
            : uiLabels.showMoreText ?? "Show more";

          let categoryBody: ReactElement;
          if (cat.key === "alphabetical") {
            categoryBody = (
              <div className="flex flex-wrap gap-[10px] max-w-[284px] pb-4">
                {visibleOptions.map((opt) => {
                  const selected = isStockFilterSelected(selectedList, opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleOption(cat.key, cat.name, opt.value)}
                      className={`h-[34px] min-w-[34px] w-fit p-2 rounded border text-sm cursor-pointer ${
                        selected
                          ? "bg-bfl-blue text-white"
                          : "border-gray-300 text-[#1A1A1A] bg-white"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            );
          } else if (cat.key === "sector") {
            categoryBody = (
              <div className="flex flex-col min-h-[300px]">
                <div className="relative mb-2 shrink-0">
                  <i
                    className="icomoon absolute left-2.5 top-1/2 -translate-y-1/2 text-[14px] text-gray-400 before:content-['\e908']"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    value={searchQueryByKey[cat.key] ?? ""}
                    onChange={(e) =>
                      setSearchQueryByKey((prev) => ({
                        ...prev,
                        [cat.key]: e.target.value,
                      }))
                    }
                    placeholder={uiLabels.searchPlaceholder ?? "Search"}
                    className="w-full rounded border border-gray-300 p-3 pl-[30px] text-[12px] outline-none"
                  />
                </div>
                <div className="flex flex-col max-h-[330px] overflow-y-auto pr-1 scrollbar-thin-gray">
                  {cat.options
                    .filter((opt) =>
                      opt.label
                        .toLowerCase()
                        .includes((searchQueryByKey[cat.key] ?? "").toLowerCase()),
                    )
                    .map((opt) => {
                      const checked = isStockFilterSelected(selectedList, opt.value);
                      return (
                        <label
                          key={opt.value}
                          className="flex cursor-pointer items-center gap-2 pb-[12px]"
                        >
                          <span className="relative inline-flex h-4 w-4 min-w-[16px]">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleOption(cat.key, cat.name, opt.value, opt.label)}
                              className="peer h-full w-full cursor-pointer appearance-none outline-none bg-white border border-[#d2d2d2] rounded-[2px] checked:bg-link-primary checked:border-link-primary"
                            />
                            <svg
                              className="pointer-events-none absolute inset-0 m-auto hidden h-4 w-4 peer-checked:block"
                              viewBox="0 0 16 16"
                              fill="none"
                              aria-hidden="true"
                            >
                              <path
                                d="M3 8l3.5 3.5L13 5"
                                stroke="white"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span className="text-[12px] text-[#002953]">{opt.label}</span>
                        </label>
                      );
                    })}
                </div>
              </div>
            );
          } else {
            categoryBody = (
              <div className="flex flex-col gap-2 pb-4">
                {visibleOptions.map((opt) => {
                  const checked = isStockFilterSelected(selectedList, opt.value);
                  return (
                    <label
                      key={opt.value}
                      className="flex cursor-pointer items-center gap-[10px]"
                    >
                      <span className="relative inline-flex h-4 w-4 min-w-[16px]">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOption(cat.key, cat.name, opt.value, opt.label)}
                          className="peer h-full w-full cursor-pointer appearance-none outline-none bg-white border border-[#d2d2d2] rounded-[2px] checked:bg-[#ff6700] checked:border-[#ff6700]"
                        />
                        <svg
                          className="pointer-events-none absolute inset-0 m-auto hidden h-4 w-4 peer-checked:block"
                          viewBox="0 0 16 16"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M3 8l3.5 3.5L13 5"
                            stroke="white"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <span className="text-[12px] text-[#002953]">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            );
          }

          return (
            <div key={cat.key} className="border-b border-[#F2F2F2] flex flex-col gap-[10px] last:border-0">
              <div className="flex w-full items-center justify-between text-left">
                <div className="text-sm font-medium text-[#1A1A1A]">
                  {cat.name}
                </div>
              </div>

              <div>
                {categoryBody}

                  {hasOverflow && cat.key !== "sector" && cat.key !== "alphabetical" && (
                    <button
                      type="button"
                      onClick={() => setShowAllByKey((prev) => ({ ...prev, [cat.key]: !prev[cat.key] }))}
                      className="mt-3 text-[12px] font-medium text-[rgb(255,103,0)] hover:underline"
                    >
                      {overflowToggleText}
                    </button>
                  )}
                </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

StockListingFiltersSidebar.displayName = "StockListingFiltersSidebar";

export default StockListingFiltersSidebar;
